import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { SeoHead } from "@/components/common/SeoHead";
import { WhatsAppCTA } from "@/components/common/WhatsAppCTA";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Check, Plane, Users, Globe, Clock } from "lucide-react";

const BREADCRUMB = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://tripsyfy.com" },
    { "@type": "ListItem", position: 2, name: "International Flight Booking", item: "https://tripsyfy.com/international-flight-booking" },
  ],
});

const FAQ_SCHEMA = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "How does Tripsyfy help with international flight booking?", acceptedAnswer: { "@type": "Answer", text: "Tripsyfy's travel experts search across airlines and routing options to find flights that match your travel dates, budget, and preferences. We handle complex itineraries, multi-city trips, and flexible date searches that online booking tools struggle with." } },
    { "@type": "Question", name: "Is it cheaper to book flights through Tripsyfy than online?", acceptedAnswer: { "@type": "Answer", text: "In many cases, yes — especially for complex itineraries, group bookings, or flexible date travel. We know how to find better routing options and flag deals that automated tools miss. Even when prices are comparable, our service saves you hours of research." } },
    { "@type": "Question", name: "Can Tripsyfy book multi-city and open-jaw flights?", acceptedAnswer: { "@type": "Answer", text: "Yes. Multi-city and open-jaw itineraries are one of our specialties. Online booking tools often price these poorly or require you to book separately. We find combined fares and the most practical routing options." } },
    { "@type": "Question", name: "Does Tripsyfy help with family travel and large groups?", acceptedAnswer: { "@type": "Answer", text: "Yes. We handle flight bookings for families with children, extended family groups, and large parties of 10 or more travelers. We ensure everyone is on the same flights or coordinated connections, with seat assignments handled." } },
    { "@type": "Question", name: "What if my travel dates are flexible?", acceptedAnswer: { "@type": "Answer", text: "Flexible dates are a huge advantage. Tell us your preferred window — we'll search across a range of dates to find the best fares. Even shifting by a day or two can save hundreds of dollars on international flights." } },
    { "@type": "Question", name: "How do I request a flight booking from Tripsyfy?", acceptedAnswer: { "@type": "Answer", text: "Call us at +1 972-666-1122, message us on WhatsApp, or submit a quote request on our website. Tell us your departure city, destination, travel dates, number of travelers, and cabin class preference — we'll get back to you with options." } },
  ],
});

const whoItIsFor = [
  { icon: Users, title: "Families traveling internationally", desc: "Coordinating seats, meals, and connections for the whole family." },
  { icon: Globe, title: "Diaspora travelers", desc: "Frequent flyers to India, Africa, and South Asia who need reliable, affordable fares." },
  { icon: Plane, title: "Multi-city trip planners", desc: "Anyone needing an open-jaw or multi-stop itinerary that online tools price poorly." },
  { icon: Clock, title: "Flexible date travelers", desc: "Travelers who can shift dates by days or weeks and want the best price across that range." },
  { icon: Users, title: "Groups of 10 or more", desc: "Corporate travel, community groups, destination weddings, and group tours." },
  { icon: Globe, title: "First-time international flyers", desc: "Travelers who want guidance on visa requirements, transit rules, and connecting flights." },
];

const whatWeHandle = [
  "Searching across airlines and route combinations for the best price",
  "Multi-city and open-jaw itinerary planning",
  "Group flight coordination for 10 or more travelers",
  "Flexible date searching across a ± range",
  "Cabin class comparison (economy, premium economy, business)",
  "Baggage allowance and checked bag fee guidance",
  "Seat assignment assistance and family seating",
  "Transit visa and layover rules guidance",
  "Booking and issuing confirmed e-tickets",
  "Post-booking support for changes and cancellations",
];

const faqs = [
  { q: "How does Tripsyfy help with international flight booking?", a: "Our travel experts search across airlines and routing options to find flights that match your dates, budget, and preferences — including complex itineraries and multi-city trips that online tools handle poorly." },
  { q: "Is it cheaper to book flights through Tripsyfy than online?", a: "In many cases, yes — especially for complex itineraries, group bookings, or flexible date travel. We find routing options and deals that automated tools miss. Even when prices match, our service saves hours of research." },
  { q: "Can Tripsyfy book multi-city and open-jaw flights?", a: "Yes. Multi-city and open-jaw itineraries are one of our specialties. We find combined fares and the most practical routing options." },
  { q: "Does Tripsyfy help with family travel and large groups?", a: "Yes. We handle flight bookings for families with children, extended family groups, and large parties of 10 or more travelers — ensuring coordinated connections and seat assignments." },
  { q: "What if my travel dates are flexible?", a: "Flexible dates are a major advantage. Tell us your preferred window and we'll search across a range of dates to find the best fares. Shifting by even a day or two can save hundreds on international flights." },
  { q: "How do I request a flight booking from Tripsyfy?", a: "Call us at +1 972-666-1122, message us on WhatsApp, or submit a quote request. Share your departure city, destination, travel dates, number of travelers, and cabin class preference." },
];

export default function InternationalFlightBookingPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div id="main-content" className="min-h-screen bg-white">
      <Helmet>
        <script type="application/ld+json">{BREADCRUMB}</script>
        <script type="application/ld+json">{FAQ_SCHEMA}</script>
      </Helmet>
      <SeoHead
        title="International Flight Booking Assistance"
        description="Book international flights with confidence. Tripsyfy helps compare routes, timing, airlines, and travel needs with human support for families, groups, and complex itineraries."
        canonical="https://tripsyfy.com/international-flight-booking"
      />
      <Navbar />

      <section className="relative pt-28 pb-14 bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <p className="text-sky-300 text-xs font-semibold tracking-widest uppercase mb-3">Flight Booking Service</p>
          <h1 className="heading-xl text-white mb-3">International Flight Booking Assistance</h1>
          <p className="text-base text-white/70 max-w-xl leading-relaxed mb-8">
            Tripsyfy provides personalized assistance for international flight bookings — comparing airlines, routes, and fares with a real human expert who understands your travel needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/contact"><Button className="bg-accent text-white hover:bg-accent/90">Request a Quote</Button></Link>
            <WhatsAppCTA variant="button" />
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-2xl mb-10">
              <p className="overline mb-3">Who This Is For</p>
              <h2 className="heading-lg text-primary">We specialize in travel that's hard to book online</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {whoItIsFor.map(({ icon: Icon, title, desc }, i) => (
              <AnimatedSection key={title} delay={i * 0.06}>
                <div className="bg-white rounded-xl shadow-card p-5 flex gap-4 items-start">
                  <div className="h-10 w-10 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-primary mb-1">{title}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <AnimatedSection>
              <p className="overline mb-3">What We Handle</p>
              <h2 className="heading-lg text-primary mb-6">Everything from search to boarding pass</h2>
              <ul className="space-y-3">
                {whatWeHandle.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="h-5 w-5 rounded-full bg-accent/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-accent" />
                    </div>
                    <span className="text-sm text-foreground leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <div className="bg-surface-subtle rounded-xl p-8">
                <p className="overline mb-3">Why Human Support Matters</p>
                <h3 className="heading-md text-primary mb-4">The limits of online booking tools</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  Online booking engines are designed for simple, one-way or round-trip searches. They miss cheaper routing options, don't account for layover risks, and can't advise on transit visas or seat strategies for families.
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                  A human travel expert can spot a routing that saves $400, flag a 45-minute connection that's too risky for an elderly parent, and coordinate a group of 12 people across multiple departure cities.
                </p>
                <WhatsAppCTA variant="banner" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-2xl mb-10">
              <p className="overline mb-3">How It Works</p>
              <h2 className="heading-lg text-primary">How we handle your international flight booking</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {[
              { num: "01", title: "Share your travel details", desc: "Tell us your departure city, destination, dates, number of travelers, and cabin class preference. We'll start from there." },
              { num: "02", title: "We research options", desc: "Our team searches across airlines, routing combinations, and date ranges to find the best flights for your needs and budget." },
              { num: "03", title: "You choose what works", desc: "We present clear, comparable options. You pick the routing, airline, and timing — no pressure or upselling." },
              { num: "04", title: "We book and confirm", desc: "We handle the full booking, send your confirmed e-tickets, and remain available for any changes or support." },
            ].map(({ num, title, desc }, i) => (
              <AnimatedSection key={num} delay={i * 0.08}>
                <div>
                  <p className="text-4xl font-bold text-primary/10 mb-2 leading-none">{num}</p>
                  <h3 className="text-sm font-bold text-primary mb-2">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
          <AnimatedSection>
            <p className="overline mb-3 text-center">Common Questions</p>
            <h2 className="heading-lg text-primary text-center mb-10">International Flight Booking</h2>
          </AnimatedSection>
          <div className="space-y-3">
            {faqs.map(({ q, a }, i) => (
              <AnimatedSection key={i} delay={i * 0.04}>
                <details className="bg-white rounded-xl shadow-card overflow-hidden">
                  <summary className="px-6 py-4 cursor-pointer font-semibold text-primary text-sm list-none flex items-center justify-between gap-4 hover:text-accent transition-colors">
                    {q}<span className="text-accent text-lg leading-none shrink-0">+</span>
                  </summary>
                  <div className="px-6 pb-5">
                    <p className="text-sm text-muted-foreground leading-relaxed">{a}</p>
                  </div>
                </details>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-center lg:text-left items-center">
            <div className="lg:col-span-2">
              <AnimatedSection>
                <p className="text-xs font-semibold tracking-widest uppercase text-accent mb-3">Book Your Flights</p>
                <h2 className="heading-lg text-white mb-3">Ready to book your international flight?</h2>
                <p className="text-white/70 text-base leading-relaxed">Share your destination, dates, and traveler details — we'll find the best options and handle the rest.</p>
              </AnimatedSection>
            </div>
            <div className="flex flex-col gap-3 lg:items-end">
              <Link to="/contact"><Button className="bg-accent text-white hover:bg-accent/90 w-full lg:w-auto">Request a Quote</Button></Link>
              <WhatsAppCTA variant="button" />
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { path: "/flights-to-india-from-usa", label: "Flights to India from USA" },
              { path: "/flights-to-africa-from-usa", label: "Flights to Africa from USA" },
              { path: "/group-travel-planning", label: "Group Travel Planning" },
            ].map(({ path, label }) => (
              <Link key={path} to={path} className="text-sm text-white/60 hover:text-white transition-colors text-center">→ {label}</Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
